<!--
  @component GatesIO — P2 effecting lens (u2 gate tier).
  chain:   ctx.deployment.dispatch("gatedDeploy", spec) → this → ctx.gates
  reads:   the 5 declared gate dimensions + stream-filtered verdicts
  effects: dispatches "gatedDeploy" (every verdict ledgered by the
           daemon). Unwired ⇒ NotWiredError (FACE-LEDGER).
-->

<script lang="ts" module>
  import { getContext } from "svelte";
  import { GATES_IO_KEY } from "./deployment-io.js";
  import type { GatesIOContext, GateDimension } from "./deployment-io.js";

  export { GATES_IO_KEY };
  export type { GatesIOContext, GateDimension };

  export function useGatesIO(): GatesIOContext {
    const ctx = getContext<GatesIOContext | undefined>(GATES_IO_KEY);
    if (!ctx) {
      throw new Error("useGatesIO() called outside of a GatesIO tree.");
    }
    return ctx;
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";
  import type { SupplyRunSpec } from "./deployment-io.js";
  // `GateDimension` is already imported in <script module> above —
  // re-importing here triggers svelte-check's duplicate-identifier
  // rule (the two script blocks share type-identifier scope).

  interface Props {
    children?: Snippet;
  }
  const { children }: Props = $props();

  const parent = useDeploymentIO();

  // The 5 declared u2 gate dimensions — the v1.9.0 truth.
  const DIMENSIONS: readonly GateDimension[] = [
    "budget",
    "freeze",
    "health",
    "attestation",
    "approval",
  ];

  let inFlight = $state(false);
  let lastVerdict: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function gatedDeploy(spec: SupplyRunSpec): Promise<unknown> {
    inFlight = true;
    lastError = null;
    try {
      const r = await parent.dispatch("gatedDeploy", spec);
      lastVerdict = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally {
      inFlight = false;
    }
  }

  const ctx: GatesIOContext = {
    dimensions: DIMENSIONS,
    get inFlight() {
      return inFlight;
    },
    get lastVerdict() {
      return lastVerdict;
    },
    get lastError() {
      return lastError;
    },
    get verdicts() {
      // gate-related ops surfaced in the unified stream
      return parent.stream.filter(
        (e) =>
          e.op === "gatedDeploy" ||
          e.op === "gate" ||
          /^gate\./.test(e.op),
      );
    },
    gatedDeploy,
  } satisfies GatesIOContext;

  setContext(GATES_IO_KEY, ctx);
</script>

{@render children?.()}
